import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Chip,
  Stack
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateNewUser, initiateUser } from '../../state/user/userSlice';
import { RootState } from '../../app/store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Action } from '@reduxjs/toolkit';

const steps = ['Your Goal', 'Basic Information', 'Preferences'];

const activityMarks = (t: (key: string) => string) => [
  { value: 1.2, label: t('Sedentary') },
  { value: 1.55, label: t('Moderately Active') },
  { value: 1.9, label: t('Very Active') },
];

interface NewClientProps {
  coupon: string;
}

function GoalStep({
  t,
  currentWeight,
  targetWeight,
  timeframe,
  setCurrentWeight,
  setTargetWeight,
  setTimeframe
}: {
  t: (key: string) => string;
  currentWeight: string;
  targetWeight: string;
  timeframe: string;
  setCurrentWeight: React.Dispatch<React.SetStateAction<string>>;
  setTargetWeight: React.Dispatch<React.SetStateAction<string>>;
  setTimeframe: React.Dispatch<React.SetStateAction<string>>;
}) {
  return (
    <Box display="flex" flexDirection="column" gap={2} width="300px">
      <Typography variant="h6">{t('Your Goal')}</Typography>
      <TextField
        required
        label={t("Current Weight (kg)")}
        type="number"
        value={currentWeight}
        onChange={(e) => setCurrentWeight(e.target.value)}
      />
      <TextField
        required
        label={t("Target Weight (kg)")}
        type="number"
        value={targetWeight}
        onChange={(e) => setTargetWeight(e.target.value)}
      />
      <TextField
        required
        label={t("Timeframe (weeks)")}
        type="number"
        value={timeframe}
        onChange={(e) => setTimeframe(e.target.value)}
      />
    </Box>
  );
}

function BasicInfoStep({
  t,
  age,
  height,
  gender,
  activityLevel,
  setAge,
  setHeight,
  setGender,
  setActivityLevel
}: {
  t: (key: string) => string;
  age: string;
  height: string;
  gender: string;
  activityLevel: number;
  setAge: React.Dispatch<React.SetStateAction<string>>;
  setHeight: React.Dispatch<React.SetStateAction<string>>;
  setGender: React.Dispatch<React.SetStateAction<string>>;
  setActivityLevel: React.Dispatch<React.SetStateAction<number>>;
}) {
  return (
    <Box display="flex" flexDirection="column" gap={2} width="300px">
      <Typography variant="h6">{t('Basic Information')}</Typography>
      <TextField
        required
        label={t("Age")}
        type="number"
        value={age}
        onChange={(e) => setAge(e.target.value)}
      />
      <TextField
        required
        label={t("Height")}
        type="number"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
      />
      <Typography>{t('Gender')}</Typography>
      <RadioGroup value={gender} onChange={(e) => setGender(e.target.value)}>
        <FormControlLabel value="MALE" control={<Radio />} label={t('Male')} />
        <FormControlLabel value="FEMALE" control={<Radio />} label={t('Female')} />
        <FormControlLabel value="OTHER" control={<Radio />} label={t('Other')} />
      </RadioGroup>

      <Typography>{t('Activity Level')}</Typography>
      <Slider
        value={activityLevel}
        onChange={(e, val) => setActivityLevel(val as number)}
        step={0.175}
        min={1.2}
        max={1.9}
        marks={activityMarks(t)}
      />
    </Box>
  );
}

function PreferencesStep({
  t,
  dietPreference,
  setDietPreference
}: {
  t: (key: string) => string;
  dietPreference: string;
  setDietPreference: React.Dispatch<React.SetStateAction<string>>;
}) {
  const preferences = ['NORMAL', 'VEGETARIAN', 'VEGAN'];
  return (
    <Box display="flex" flexDirection="column" gap={2} width="300px">
      <Typography variant="h6">{t('Dietary Preferences')}</Typography>
      <Stack direction="row" spacing={1}>
        {preferences.map((pref) => (
          <Chip
            key={pref}
            label={t(pref)}
            clickable
            color={dietPreference === pref ? "primary" : "default"}
            onClick={() => setDietPreference(pref)}
          />
        ))}
      </Stack>
    </Box>
  );
}

export function NewClient({ coupon }: NewClientProps) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.newUser);

  const [activeStep, setActiveStep] = useState(0);

  // State for Step 1
  const [currentWeight, setCurrentWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [timeframe, setTimeframe] = useState('');

  // State for Step 2
  const [age, setAge] = useState('');
  const [currentHeight, setCurrentHeight] = useState('');
  const [gender, setGender] = useState('MALE');
  const [activityLevel, setActivityLevel] = useState(1.2);

  // State for Step 3
  const [dietPreference, setDietPreference] = useState('NORMAL');

  const validateStep = () => {
    if (activeStep === 0 && (!currentWeight || !targetWeight || !timeframe)) {
      alert(t('Please fill out current weight, target weight, and timeframe.'));
      return false;
    }
    if (activeStep === 1 && !age) {
      alert(t('Please fill out your age.'));
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = () => {
    dispatch(updateNewUser({ name: 'currentWeight', value: currentWeight }));
    dispatch(updateNewUser({ name: 'plannedWeight', value: targetWeight }));
    dispatch(updateNewUser({ name: 'timeframe', value: timeframe }));
    dispatch(updateNewUser({ name: 'age', value: age }));
    dispatch(updateNewUser({ name: 'currentHeight', value: currentHeight }));
    dispatch(updateNewUser({ name: 'gender', value: gender }));
    dispatch(updateNewUser({ name: 'activityLevel', value: activityLevel }));
    dispatch(updateNewUser({ name: 'dietPreference', value: dietPreference }));

    dispatch(initiateUser(user)).then((action: Action<string>) => {
      if (initiateUser.fulfilled.match(action)) {
        navigate('/user/onboarding/dashboard');
      }
    })
  };

  const renderStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <GoalStep
            t={t}
            currentWeight={currentWeight}
            targetWeight={targetWeight}
            timeframe={timeframe}
            setCurrentWeight={setCurrentWeight}
            setTargetWeight={setTargetWeight}
            setTimeframe={setTimeframe}
          />
        );
      case 1:
        return (
          <BasicInfoStep
            t={t}
            age={age}
            height={currentHeight}
            gender={gender}
            activityLevel={activityLevel}
            setAge={setAge}
            setHeight={setCurrentHeight}
            setGender={setGender}
            setActivityLevel={setActivityLevel}
          />
        );
      case 2:
        return (
          <PreferencesStep
            t={t}
            dietPreference={dietPreference}
            setDietPreference={setDietPreference}
          />
        );
      default:
        return <Box>{t('Unknown step')}</Box>;
    }
  };

  return (
    <Box
      sx={{
        marginTop: 8,
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#FBF7F2',
        width: '100%'
      }}
    >
      <Stepper activeStep={activeStep} alternativeLabel sx={{ marginBottom: 4, width: '100%' }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{t(label)}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box display="flex" justifyContent="space-between" width="300px" marginTop={4}>
        {activeStep > 0 && (
          <Button variant="outlined" onClick={handleBack}>
            {t('Back')}
          </Button>
        )}
        <Button variant="contained" color="success" onClick={handleNext}>
          {activeStep === steps.length - 1 ? t('Finish') : t('Next')}
        </Button>
      </Box>
    </Box>
  );
}
